import { FC, useState } from 'react';
import { useMutation } from '@apollo/client';

import { AccountingExportOption } from '__generated__/GQL';
import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import BackButton from 'components/BackButton';
import StepperProgressBar from 'components/Stepper/StepperProgressBar';
import { StepProps } from 'components/Stepper/types';
import useToast from 'components/ToastProvider/useToast';
import logger from 'helpers/logger';
import * as Layout from 'layouts/StepperLayout';

import ExportOptions from '../../components/ExportOptions';
import { useExportCreationContext } from '../../hooks/useExportCreationContext';
import { CreateAccountingExportDocument } from './hooks/createAccountingExport/createAccountingExport.gql';
import useCreateAccountingRecurringExport from './hooks/createAccountingRecurringExport';
import locales from './locales';

const SelectContent: FC<StepProps> = ({ goBack, goNext }) => {
  const {
    flowConfig: { isRecurring },
    setSharedState,
    sharedState,
  } = useExportCreationContext();

  const { bankAccountId, companyProfileId } = sharedState;

  const { showToast } = useToast();

  const [selectedContentIds, setSelectedContentIds] = useState<
    AccountingExportOption[]
  >(sharedState.selectedContentIds);

  const [createAccountingRecurringExport, createAccountingRecurringExportHook] =
    useCreateAccountingRecurringExport(companyProfileId);

  const [createAccountingExportMutation, createAccountingExportMutationHook] =
    useMutation(CreateAccountingExportDocument);

  const onCreateAccountingExport = async () => {
    const { email, endDate, name, startDate } = sharedState;

    try {
      if (isRecurring) {
        await createAccountingRecurringExport({
          variables: {
            input: {
              bankAccountId,
              companyProfileId,
              options: selectedContentIds,
              recipient: { email, name },
            },
          },
        });
      } else {
        await createAccountingExportMutation({
          variables: {
            input: {
              bankAccountId,
              companyProfileId,
              endDate,
              options: selectedContentIds,
              recipient: { email, name },
              startDate,
            },
          },
        });
      }

      setSharedState((prevState) => ({
        ...prevState,
        selectedContentIds,
      }));

      goNext();
    } catch (error) {
      logger.error(error);
      showToast({ message: locales.exportCreationError, type: 'error' });
    }
  };

  return (
    <Layout.Root>
      <Layout.Body>
        <Layout.HeaderButtonsContainer>
          <BackButton onClick={goBack} />
        </Layout.HeaderButtonsContainer>

        <Layout.BodyScrollableSection>
          <Heading marginBottom="space-32" size="lg">
            {locales.title}
          </Heading>

          <ExportOptions
            onChange={setSelectedContentIds}
            selectedIds={selectedContentIds}
            withBankStatement={isRecurring}
          />
        </Layout.BodyScrollableSection>

        <Layout.FooterContainer>
          <StepperProgressBar />

          <Layout.FooterButtonsContainer>
            <Button
              icon="send"
              isDisabled={selectedContentIds.length === 0}
              isLoading={
                createAccountingRecurringExportHook.loading ||
                createAccountingExportMutationHook.loading
              }
              onClick={onCreateAccountingExport}
            >
              {locales.nextButton}
            </Button>
          </Layout.FooterButtonsContainer>
        </Layout.FooterContainer>
      </Layout.Body>
    </Layout.Root>
  );
};

export default SelectContent;
